// src/pages/PrivacyPolicyPage.jsx
import { Link } from "react-router-dom";
import "./PrivacyPolicyPage.css";

const SECTIONS = [
  {
    title: "1. Who We Are",
    content: `Circuit Printing Solutions ("we", "us", "our") is a UK-based printing company. Our registered address is available upon request. We are the data controller for the personal information we collect from you through our website and services.

For any privacy-related queries, please contact us at: privacy@circuitprinting.co.uk`
  },
  {
    title: "2. Information We Collect",
    content: `We collect the following types of personal information:

Account Information: Your name, email address, and password when you create an account.

Order Information: Delivery address, phone number, and payment details (processed securely by Stripe — we do not store card details).

Communication Data: Messages you send us via our contact form or email.

Usage Data: Information about how you use our website, including IP address, browser type, pages visited, and time spent on pages (collected via cookies).

Artwork Files: Print files you upload for your orders, which are stored securely and deleted after your order is completed.`
  },
  {
    title: "3. How We Use Your Information",
    content: `We use your personal information to:

• Process and fulfil your orders
• Communicate with you about your orders and enquiries
• Send order confirmations and delivery updates
• Manage your account
• Improve our website and services
• Comply with legal obligations
• Prevent fraud and ensure security

We do not sell, rent, or trade your personal information to third parties for marketing purposes.`
  },
  {
    title: "4. Legal Basis for Processing",
    content: `Under UK GDPR, we process your data on the following legal bases:

Contract Performance: Processing necessary to fulfil your orders and provide our services.

Legitimate Interests: Improving our services, preventing fraud, and ensuring website security.

Legal Obligation: Complying with applicable laws, including tax and financial regulations.

Consent: Where you have explicitly consented, such as subscribing to our newsletter.`
  },
  {
    title: "5. Data Sharing",
    content: `We share your data only with trusted third parties necessary to provide our services:

Stripe: For secure payment processing. Stripe is PCI DSS compliant.

MySQL and our application API: For our database, authentication, and file storage infrastructure.

Courier Partners: Your name and delivery address are shared with our delivery partners to fulfil shipments.

We require all third parties to respect the security of your personal data and treat it in accordance with applicable law.`
  },
  {
    title: "6. Data Retention",
    content: `We retain your personal information for as long as necessary to provide our services and comply with our legal obligations:

Account Data: Retained for the duration of your account plus 2 years after closure.

Order Data: Retained for 7 years for tax and legal compliance purposes.

Artwork Files: Deleted within 90 days of order completion.

Communication Records: Retained for 3 years.`
  },
  {
    title: "7. Your Rights",
    content: `Under UK GDPR, you have the following rights:

Right of Access: Request a copy of the personal data we hold about you.

Right to Rectification: Request correction of inaccurate or incomplete data.

Right to Erasure: Request deletion of your personal data (subject to legal obligations).

Right to Restrict Processing: Request that we limit how we use your data.

Right to Data Portability: Request your data in a machine-readable format.

Right to Object: Object to our processing of your data in certain circumstances.

To exercise any of these rights, please contact us at privacy@circuitprinting.co.uk. We will respond within 30 days.`
  },
  {
    title: "8. Cookies",
    content: `We use cookies and similar technologies to improve your browsing experience. Cookies we use include:

Essential Cookies: Required for the website to function (login sessions, cart contents).

Analytics Cookies: Help us understand how visitors use our site (e.g., Google Analytics).

You can control cookies through your browser settings. Disabling certain cookies may affect website functionality.`
  },
  {
    title: "9. Security",
    content: `We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, loss, or destruction. These include SSL encryption, secure authentication, and regular security reviews.

However, no internet transmission is completely secure. We cannot guarantee the absolute security of data transmitted to our website.`
  },
  {
    title: "10. Changes to This Policy",
    content: `We may update this Privacy Policy from time to time. We will notify you of significant changes by email or by posting a notice on our website. The date of the most recent revision is shown below.

Last updated: June 2025`
  },
  {
    title: "11. Contact Us",
    content: `If you have any questions about this Privacy Policy or how we handle your data, please contact us:

Email: privacy@circuitprinting.co.uk
Address: Available upon request

If you are not satisfied with our response, you have the right to lodge a complaint with the Information Commissioner's Office (ICO): ico.org.uk`
  },
];

export default function PrivacyPolicyPage() {
  return (
    <div className="pp-page">
      {/* Hero */}
      <div className="pp-hero anim-fade-in-up">
        <div className="container">
          <span className="tag">Legal</span>
          <h1>Privacy Policy</h1>
          <p>Last updated: June 2025</p>
        </div>
      </div>

      <div className="container pp-layout">
        {/* Sidebar nav */}
        <div className="pp-sidebar">
          <div className="pp-sidebar-box">
            <p className="pp-sidebar-title">On This Page</p>
            {SECTIONS.map(function(s) {
              return (
                <a key={s.title} href={"#" + s.title.replace(/\s+/g, "-").toLowerCase()} className="pp-sidebar-link">
                  {s.title}
                </a>
              );
            })}
          </div>
          <div className="pp-sidebar-contact">
            <p>Questions about your data?</p>
            <Link to="/contact" className="btn-primary" style={{ display: "block", textAlign: "center", marginTop: 10 }}>
              Contact Us
            </Link>
          </div>
        </div>

        {/* Content */}
        <div className="pp-content">
          <div className="pp-intro">
            <p>At Circuit Printing Solutions, we are committed to protecting your privacy and handling your personal data responsibly. This policy explains what information we collect, how we use it, and your rights under UK data protection law (UK GDPR).</p>
          </div>

          {SECTIONS.map(function(s) {
            return (
              <div key={s.title} id={s.title.replace(/\s+/g, "-").toLowerCase()} className="pp-section">
                <h2>{s.title}</h2>
                <p>{s.content}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
